// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

/**
 * Warp Travel Calculation Utilities
 * Pure functions for warp factor optimization and travel time calculations
 */

import GameConfig from '../../config/GameConfig';
import * as NavUtils from './NavigationUtils';

/**
 * Calculate optimal warp factor based on distance and available fuel
 * Prioritizes fuel efficiency over speed - finds the LOWEST warp that can reach destination
 * 
 * @param {number} distance - Distance to destination in light-years
 * @param {number} availableFuel - Current fuel percentage
 * @param {number} maxFuel - Maximum fuel capacity percentage
 * @param {number} fuelEfficiencyBonus - Fuel efficiency bonus from upgrades (0-1, where 0.1 = 10% less fuel)
 * @returns {number} Optimal warp factor (1-9)
 */
export const calculateOptimalWarp = (distance, availableFuel, maxFuel = 100, fuelEfficiencyBonus = 0) => {
  // Start with distance-based warp selection (minimum recommended)
  const thresholds = GameConfig.warp.autoModeThresholds;
  let minRecommendedWarp;
  
  if (distance < thresholds.warp2Max) minRecommendedWarp = 2;
  else if (distance < thresholds.warp3Max) minRecommendedWarp = 3;
  else if (distance < thresholds.warp4Max) minRecommendedWarp = 4;
  else if (distance < thresholds.warp5Max) minRecommendedWarp = 5;
  else minRecommendedWarp = 6;
  
  // Find the LOWEST warp factor that can reach the destination with current fuel
  // This prioritizes fuel efficiency over speed
  for (let testWarp = minRecommendedWarp; testWarp <= GameConfig.warp.maxSafeWarp; testWarp++) {
    const fuelNeeded = NavUtils.calculateFuelNeeded(distance, testWarp, fuelEfficiencyBonus);
    if (fuelNeeded <= availableFuel) {
      return testWarp; // Found the most fuel-efficient warp we can afford
    }
  }
  
  // If we can't afford the recommended warp, try lower warp factors
  for (let testWarp = minRecommendedWarp - 1; testWarp >= 1; testWarp--) {
    const fuelNeeded = NavUtils.calculateFuelNeeded(distance, testWarp, fuelEfficiencyBonus);
    if (fuelNeeded <= availableFuel) {
      return testWarp; // Found a warp factor we can afford (slower but possible)
    }
  }
  
  // If we can't afford any warp factor, return 1 (will trigger warning)
  return 1;
};

/**
 * Calculate travel time in seconds based on distance and warp factor
 * Applies engine damage penalties if provided
 * 
 * @param {number} distance - Distance in light-years
 * @param {number} warpFactor - Warp factor (1-9)
 * @param {number} warpSpeedPenalty - Engine damage penalty (0-1, where 1.0 = no penalty, 0.5 = 50% slower)
 * @returns {number} Travel time in seconds (clamped between min and max from config)
 */
export const calculateTravelTime = (distance, warpFactor, warpSpeedPenalty = 1.0) => {
  // Apply engine damage penalty
  const effectiveWarpFactor = warpFactor * warpSpeedPenalty;
  
  // Formula: time = distance / (warpFactor^2) with a scaling factor
  const baseTime = distance / Math.pow(effectiveWarpFactor, 2);
  const scaledTime = Math.max(
    GameConfig.warp.minTravelTime, 
    Math.min(GameConfig.warp.maxTravelTime, baseTime * GameConfig.warp.travelTimeScaleFactor)
  );
  return Math.round(scaledTime);
};

/**
 * Get default warp factor based on navigation mode
 * 
 * @param {string} navigationMode - Navigation mode (AUTO/MANUAL/EMERGENCY)
 * @param {number} distance - Distance to destination (optional, required for AUTO mode)
 * @param {number} availableFuel - Current fuel percentage (optional, required for AUTO mode)
 * @param {number} maxFuel - Maximum fuel capacity (optional, for AUTO mode)
 * @param {number} fuelEfficiencyBonus - Fuel efficiency bonus from upgrades (optional)
 * @returns {number} Default warp factor
 */
export const getDefaultWarpFactor = (
  navigationMode, 
  distance = null, 
  availableFuel = null,
  maxFuel = 100,
  fuelEfficiencyBonus = 0
) => {
  if (navigationMode === 'AUTO' && distance !== null && availableFuel !== null) {
    return calculateOptimalWarp(distance, availableFuel, maxFuel, fuelEfficiencyBonus);
  } else if (navigationMode === 'EMERGENCY') {
    return 8; // Emergency default: maximum safe speed (can still manually select Warp 9)
  } else {
    return 1; // Manual mode default
  }
};
